import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Input,
  Popover,
  Segmented,
  Select,
  Switch,
  Table,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  AimOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  EyeOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import ProjectPageShell, { useProjectContext } from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import { GenericStatusPill } from "../shared/statusColors";
import { useWbsRecordName } from "../shared/useWbsCodes";
import WbsCodeToggle from "../shared/WbsCodeToggle";
import { useFontScale } from "../../../theme/fontScale";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import {
  TaskMonitoringTask,
  TaskMonitoringTree,
  taskMonitoringApis,
} from "../../../api/TaskMonitoring";
import { fmtQty } from "./executionNorm";
import {
  MONITORING_STATUS_TONES,
  MonitoringDraft,
  MonitoringEdits,
  MonitoringFilter,
  TreeRow,
  allTasksOf,
  applyDraftEdits,
  buildMonitoringRows,
  countMonitoring,
  draftProblem,
  effectiveDraft,
  groupKeysOf,
  groupSummary,
  invalidTaskIds,
  monitoringStatus,
  normalizeTaskMonitoringTree,
  toSaveItems,
  trackOff,
  trackOn,
} from "./taskMonitoring";
import "./execution.css";
import "./taskMonitoring.css";

// Task Monitoring — the per-task "ask the site about this line" switch, with
// an active window, chosen on the Phase -> Activity -> Task tree.
//
// Monitoring is a FOCUS filter only. It decides which BOQ lines the daily
// capture screens put in front of the site; it never changes what the norms
// derive. That is why this page has no numbers of its own beyond counts — it
// is a settings page, and the register stays on Daily Execution.
//
// Editing model: an edits map keyed by taskId holds ONLY rows that differ from
// the saved tree, so "n changes" is literally its size, Discard is one reset,
// and Save posts exactly those rows (the endpoint is an UPSERT per item, not a
// replace-set — sending the untouched 990 rows of a 1000-row project would be
// both slow and a lie about what the user did).

const DATE_FMT = "DD-MMM-YYYY";
const WIRE_FMT = "YYYY-MM-DD";

const toWire = (value: Dayjs | null): string => (value ? value.format(WIRE_FMT) : "");
const fromWire = (value: string): Dayjs | null => (value ? dayjs(value) : null);

const FILTER_OPTIONS: Array<{ label: string; value: MonitoringFilter }> = [
  { label: "All", value: "all" },
  { label: "Tracked", value: "tracked" },
  { label: "Untracked", value: "untracked" },
  { label: "Missing exec norm", value: "missingNorm" },
];

const TaskMonitoringPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { projectId: contextProjectId, projectName } = useProjectContext();
  const projectId = Number(params.projectId) || contextProjectId;

  const [tree, setTree] = useState<TaskMonitoringTree | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // The as-of date only changes the "active on date" evaluation. Kept as a
  // Dayjs for the picker; every comparison below uses its wire string.
  const [asOf, setAsOf] = useState<Dayjs>(() => dayjs());
  const asOfWire = asOf.format(WIRE_FMT);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MonitoringFilter>("all");
  // Phase / activity narrowing — client-side over the loaded tree, like the
  // search. Picking a phase also narrows the activity options.
  const [phaseFilter, setPhaseFilter] = useState<number | undefined>();
  const [activityFilter, setActivityFilter] = useState<number | undefined>();
  const [edits, setEdits] = useState<MonitoringEdits>({});
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFrom, setBulkFrom] = useState<Dayjs | null>(null);
  const [bulkTo, setBulkTo] = useState<Dayjs | null>(null);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  const load = useCallback(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    taskMonitoringApis
      .getTree(projectId, asOfWire)
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          message.error(res?.message || "Could not load task monitoring");
          return;
        }
        setTree(normalizeTaskMonitoringTree(res.data));
      })
      .catch(() => !cancelled && message.error("Could not load task monitoring"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, asOfWire]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  // ---- labels: honour the app-wide "show WBS codes" toggle ----
  const phases = useMemo(() => tree?.phases || [], [tree]);
  const activities = useMemo(
    () => phases.flatMap((phase) => phase.activities),
    [phases]
  );
  const phaseLabel = useWbsRecordName(phases, "phaseId", "phaseName");
  const activityLabel = useWbsRecordName(activities, "activityId", "activityName");

  const phaseOptions = useMemo(
    () =>
      phases.map((phase) => ({
        value: phase.phaseId,
        label: phase.phaseId ? phaseLabel(phase) : phase.phaseName || "No phase",
      })),
    [phases, phaseLabel]
  );
  const activityOptions = useMemo(
    () =>
      phases
        .filter((phase) => !phaseFilter || phase.phaseId === phaseFilter)
        .flatMap((phase) => phase.activities)
        .map((activity) => ({ value: activity.activityId, label: activityLabel(activity) })),
    [phases, phaseFilter, activityLabel]
  );

  // ---- the tree the table renders ----
  const rows = useMemo(
    () => buildMonitoringRows(tree, search, filter, phaseFilter, activityFilter),
    [tree, search, filter, phaseFilter, activityFilter]
  );
  const groupKeys = useMemo(() => groupKeysOf(rows), [rows]);
  // Every group open on arrival, and again whenever the SHAPE of the tree
  // changes (a new search or filter). Keyed on the joined keys rather than the
  // rows array so an edit — which re-renders rows but not their keys — never
  // undoes a fold the user made by hand.
  const groupSignature = groupKeys.join("|");
  useEffect(() => {
    setExpandedKeys(groupSignature ? groupSignature.split("|") : []);
  }, [groupSignature]);

  const visibleTasks = useMemo(() => rows.flatMap((phase) => phase.tasks), [rows]);
  const allTasks = useMemo(() => allTasksOf(tree), [tree]);
  const counts = useMemo(() => countMonitoring(tree, edits, asOfWire), [tree, edits, asOfWire]);
  const changeCount = Object.keys(edits).length;
  const invalidIds = useMemo(() => invalidTaskIds(edits), [edits]);
  const invalidSet = useMemo(() => new Set(invalidIds), [invalidIds]);

  const changeBreakdown = useMemo(() => {
    let toTrack = 0;
    let toUntrack = 0;
    let windowEdits = 0;
    const byId = new Map(allTasks.map((task) => [task.taskId, task]));
    Object.entries(edits).forEach(([taskId, draft]) => {
      const saved = byId.get(Number(taskId));
      if (!saved) return;
      if (draft.tracked && !saved.tracked) toTrack += 1;
      else if (!draft.tracked && saved.tracked) toUntrack += 1;
      else windowEdits += 1;
    });
    return { toTrack, toUntrack, windowEdits };
  }, [edits, allTasks]);

  // ---- editing ----
  const mutate = useCallback(
    (
      tasks: TaskMonitoringTask[],
      fn: (current: MonitoringDraft, task: TaskMonitoringTask) => MonitoringDraft
    ) => setEdits((current) => applyDraftEdits(current, tasks, fn)),
    []
  );

  const setTracked = (tasks: TaskMonitoringTask[], tracked: boolean) =>
    mutate(tasks, (current, task) =>
      tracked ? trackOn(current, asOfWire) : trackOff(current, task)
    );

  const setField = (
    task: TaskMonitoringTask,
    patch: Partial<Pick<MonitoringDraft, "activeFrom" | "activeTo" | "remarks">>
  ) => mutate([task], (current) => ({ ...current, ...patch }));

  // Bulk actions operate on the rows IN VIEW, so the search and filter double
  // as the selection — "everything under this phase that is untracked" is one
  // search plus one click.
  const trackedInView = useMemo(
    () => visibleTasks.filter((task) => effectiveDraft(task, edits).tracked),
    [visibleTasks, edits]
  );
  const normedUntrackedInView = useMemo(
    () =>
      visibleTasks.filter(
        (task) => task.hasExecutionNorm && !effectiveDraft(task, edits).tracked
      ),
    [visibleTasks, edits]
  );

  const applyBulkWindow = () => {
    if (!bulkFrom) {
      message.error("Pick the date the window starts");
      return;
    }
    if (bulkTo && bulkTo.isBefore(bulkFrom, "day")) {
      message.error("Active to must be on or after Active from");
      return;
    }
    const from = toWire(bulkFrom);
    const to = toWire(bulkTo);
    mutate(trackedInView, (current) => ({ ...current, activeFrom: from, activeTo: to }));
    message.success(`Window applied to ${trackedInView.length} tracked line${trackedInView.length === 1 ? "" : "s"}`);
    setBulkOpen(false);
  };

  const trackAllNormed = () => {
    setTracked(normedUntrackedInView, true);
    message.success(
      `${normedUntrackedInView.length} line${normedUntrackedInView.length === 1 ? "" : "s"} with an execution norm marked for tracking`
    );
    setBulkOpen(false);
  };

  const discard = () => setEdits({});

  const save = async () => {
    if (!projectId || !changeCount) return;
    if (invalidIds.length) {
      message.error(
        `${invalidIds.length} row${invalidIds.length === 1 ? " needs" : "s need"} a valid window before saving`
      );
      return;
    }
    setSaving(true);
    try {
      const res = await taskMonitoringApis.save({ projectId, items: toSaveItems(edits) });
      if (res?.status !== true) {
        message.error(res?.message || "Could not save task monitoring");
        return;
      }
      message.success(res.data?.message || "Task monitoring saved");
      // Non-blocking by design — e.g. tracked lines with no execution norm.
      if (res.data?.warning) message.warning({ content: res.data.warning, duration: 8 });
      setEdits({});
      load();
    } catch (err: any) {
      message.error(err?.message || "Could not save task monitoring");
    } finally {
      setSaving(false);
    }
  };

  // ---- columns ----
  // scale drives widths too: at A+/A++ the sequence ids and dates overflow
  // fixed-width columns unless the grid grows with the text. scroll.x MUST be
  // the sum of the SAME rounded widths or the frozen column misaligns.
  const { fs, scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  const widths = {
    task: w(340),
    uom: w(80),
    qty: w(110),
    norms: w(200),
    track: w(84),
    from: w(150),
    to: w(150),
    status: w(140),
    remarks: w(220),
  };
  const scrollX = Object.values(widths).reduce((sum, width) => sum + width, 0);
  const isToday = asOf.isSame(dayjs(), "day");
  const activeLabel = isToday ? "Active today" : "Active on date";

  const columns: ColumnsType<TreeRow> = [
    {
      title: "Task",
      key: "task",
      width: widths.task,
      fixed: "left",
      render: (_: unknown, row) => {
        if (row.level === "task") {
          const { task } = row;
          return (
            <div className="pms-tm-task">
              <span className="pms-tm-seq" style={{ fontSize: fs(11.5) }}>
                {task.taskSequenceId || "—"}
              </span>
              <span className="pms-tm-task-name" style={{ fontSize: fs(13) }}>
                {task.taskName}
              </span>
            </div>
          );
        }
        const summary = groupSummary(row.tasks, edits, asOfWire);
        const name =
          row.level === "phase"
            ? row.phase.phaseId === 0 || !row.phase.phaseName
              ? "No phase"
              : phaseLabel(row.phase)
            : activityLabel(row.activity);
        return (
          <span>
            <span className="pms-tm-group-name">{name}</span>
            <span className="pms-row-meta">
              {summary.tracked} of {summary.total} tracked
              {summary.tracked > 0 ? ` · ${summary.active} active` : ""}
            </span>
          </span>
        );
      },
    },
    {
      title: "UOM",
      key: "uom",
      width: widths.uom,
      render: (_: unknown, row) =>
        row.level === "task" ? (
          row.task.workUomName || <span className="pms-tm-muted">—</span>
        ) : null,
    },
    {
      title: "Work qty",
      key: "qty",
      width: widths.qty,
      align: "right",
      render: (_: unknown, row) =>
        row.level === "task" ? (
          <span className={`pms-tm-num${row.task.workQuantity ? "" : " pms-tm-muted"}`}>
            {fmtQty(row.task.workQuantity)}
          </span>
        ) : null,
    },
    {
      title: "Norms",
      key: "norms",
      width: widths.norms,
      render: (_: unknown, row) => {
        if (row.level !== "task") return null;
        const { task } = row;
        const draft = effectiveDraft(task, edits);
        return (
          <div className="pms-tm-chips">
            {task.hasExecutionNorm ? (
              <Tooltip title="Has an execution norm — derives from the face's daily advance">
                <span className="pms-tm-chip pms-tm-chip--exec">Exec</span>
              </Tooltip>
            ) : draft.tracked ? (
              <Tooltip title="Tracked, but this line has no execution norm. It will never appear in Civil until one is configured.">
                <span className="pms-tm-chip pms-tm-chip--warn">
                  <WarningOutlined /> no exec norm
                </span>
              </Tooltip>
            ) : null}
            {task.materialLineCount > 0 && (
              <span className="pms-tm-chip pms-tm-chip--neutral">Mat {task.materialLineCount}</span>
            )}
            {task.manpowerLineCount > 0 && (
              <span className="pms-tm-chip pms-tm-chip--neutral">Man {task.manpowerLineCount}</span>
            )}
            {task.machineryLineCount > 0 && (
              <span className="pms-tm-chip pms-tm-chip--neutral">Equip {task.machineryLineCount}</span>
            )}
            {!task.hasExecutionNorm &&
              !draft.tracked &&
              !task.materialLineCount &&
              !task.manpowerLineCount &&
              !task.machineryLineCount && <span className="pms-tm-muted">—</span>}
          </div>
        );
      },
    },
    {
      title: "Track",
      key: "track",
      width: widths.track,
      align: "center",
      render: (_: unknown, row) => {
        if (row.level === "task") {
          const draft = effectiveDraft(row.task, edits);
          return (
            <Switch
              size="small"
              checked={draft.tracked}
              onChange={(checked) => setTracked([row.task], checked)}
              aria-label={`Track ${row.task.taskSequenceId}`}
            />
          );
        }
        // Tri-state over every task beneath: all / none / some.
        const summary = groupSummary(row.tasks, edits, asOfWire);
        const all = summary.total > 0 && summary.tracked === summary.total;
        const some = summary.tracked > 0 && !all;
        return (
          <Tooltip title={all ? "Untrack every line beneath" : "Track every line beneath"}>
            <Checkbox
              checked={all}
              indeterminate={some}
              onChange={() => setTracked(row.tasks, !all)}
              aria-label={all ? "Untrack all beneath" : "Track all beneath"}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Active from",
      key: "from",
      width: widths.from,
      render: (_: unknown, row) => {
        if (row.level !== "task") return null;
        const draft = effectiveDraft(row.task, edits);
        // Untracked rows carry no window, so they get a dash rather than a
        // disabled picker: on a 1,000-line project that is 2,000 fewer antd
        // pickers mounted for nothing, and the row reads as "off" at a glance.
        if (!draft.tracked) return <span className="pms-tm-muted">—</span>;
        const problem = draftProblem(draft);
        return (
          <div>
            <DatePicker
              size="small"
              value={fromWire(draft.activeFrom)}
              onChange={(value) => setField(row.task, { activeFrom: toWire(value) })}
              format={DATE_FMT}
              allowClear={false}
              placeholder="Start"
              status={problem === "missingFrom" ? "error" : undefined}
              style={{ width: "100%" }}
            />
            {problem === "missingFrom" && (
              <div className="pms-tm-field-error">Start date is required</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Active to",
      key: "to",
      width: widths.to,
      render: (_: unknown, row) => {
        if (row.level !== "task") return null;
        const draft = effectiveDraft(row.task, edits);
        if (!draft.tracked) return <span className="pms-tm-muted">—</span>;
        const problem = draftProblem(draft);
        const from = fromWire(draft.activeFrom);
        return (
          <div>
            <DatePicker
              size="small"
              value={fromWire(draft.activeTo)}
              onChange={(value) => setField(row.task, { activeTo: toWire(value) })}
              format={DATE_FMT}
              allowClear
              placeholder="Open-ended"
              disabledDate={(day) => (from ? day.isBefore(from, "day") : false)}
              status={problem === "toBeforeFrom" ? "error" : undefined}
              style={{ width: "100%" }}
            />
            {problem === "toBeforeFrom" && (
              <div className="pms-tm-field-error">Must be on or after Active from</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: widths.status,
      render: (_: unknown, row) => {
        if (row.level !== "task") return null;
        const status = monitoringStatus(effectiveDraft(row.task, edits), asOfWire);
        const label =
          status === "active"
            ? activeLabel
            : status === "scheduled"
              ? "Scheduled"
              : status === "expired"
                ? "Expired"
                : status === "noWindow"
                  ? "No start date"
                  : "Off";
        return <GenericStatusPill label={label} tone={MONITORING_STATUS_TONES[status]} />;
      },
    },
    {
      title: "Remarks",
      key: "remarks",
      width: widths.remarks,
      render: (_: unknown, row) => {
        if (row.level !== "task") return null;
        const draft = effectiveDraft(row.task, edits);
        if (!draft.tracked) return <span className="pms-tm-muted">—</span>;
        return (
          <Input
            size="small"
            value={draft.remarks}
            onChange={(event) => setField(row.task, { remarks: event.target.value })}
            maxLength={250}
            placeholder="Why this line is watched"
          />
        );
      },
    },
  ];

  const rowClassName = (row: TreeRow) => {
    const classes = [`pms-tm-row-${row.level}`];
    if (row.level === "task") {
      if (invalidSet.has(row.task.taskId)) classes.push("pms-tm-row-invalid");
      else if (edits[row.task.taskId]) classes.push("pms-tm-row-dirty");
    }
    return classes.join(" ");
  };

  const noTasks = !!tree && tree.taskCount === 0;
  const allExpanded = groupKeys.length > 0 && expandedKeys.length >= groupKeys.length;

  const bulkContent = (
    <div className="pms-tm-bulk">
      <div className="pms-tm-bulk-title">Set window for tracked lines in view</div>
      <div className="pms-tm-bulk-hint">
        Applies to the {trackedInView.length} tracked line{trackedInView.length === 1 ? "" : "s"} the
        search and filter currently show.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <DatePicker
          value={bulkFrom}
          onChange={setBulkFrom}
          format={DATE_FMT}
          placeholder="Active from"
          style={{ flex: 1 }}
        />
        <DatePicker
          value={bulkTo}
          onChange={setBulkTo}
          format={DATE_FMT}
          placeholder="Open-ended"
          allowClear
          disabledDate={(day) => (bulkFrom ? day.isBefore(bulkFrom, "day") : false)}
          style={{ flex: 1 }}
        />
      </div>
      <Button
        type="primary"
        icon={<CalendarOutlined />}
        disabled={!trackedInView.length || !bulkFrom}
        onClick={applyBulkWindow}
      >
        Apply window
      </Button>
      <Divider style={{ margin: "4px 0" }} />
      <div className="pms-tm-bulk-title">Quick fill</div>
      <div className="pms-tm-bulk-hint">
        Marks every untracked line in view that has an execution norm, starting on the as-of
        date. Lines without a norm are left alone — they would never surface in Civil.
      </div>
      <Button
        icon={<ThunderboltOutlined />}
        disabled={!normedUntrackedInView.length}
        onClick={trackAllNormed}
      >
        Track all lines with an execution norm ({normedUntrackedInView.length})
      </Button>
    </div>
  );

  return (
    <ProjectPageShell
      headTitle="Task Monitoring"
      breadcrumb={[
        { label: projectName, onClick: () => navigate(-1) },
        { label: "Task Monitoring" },
      ]}
      eyebrow="Execution & Actuals"
      title="Task Monitoring"
      onBack={() => navigate(-1)}
      loading={loading && !tree}
      stats={
        <>
          <StatCard
            label="Tracked tasks"
            value={
              <span>
                {counts.trackedCount}
                <span style={{ color: "#8094ae", fontWeight: 500 }}> / {counts.taskCount}</span>
              </span>
            }
            icon={<EyeOutlined />}
          />
          <StatCard
            label={isToday ? "Active today" : `Active on ${asOf.format("DD MMM")}`}
            value={counts.activeOnDateCount}
            icon={<AimOutlined />}
          />
          <StatCard
            label="Faces with tracked lines"
            value={counts.activitiesWithTrackedCount}
            icon={<ApartmentOutlined />}
          />
          <Tooltip title="These lines will never appear in Civil until an execution norm is configured">
            <div>
              <StatCard
                label="Tracked, no execution norm"
                value={counts.trackedWithoutNormCount}
                icon={<WarningOutlined />}
                tone={counts.trackedWithoutNormCount > 0 ? "onhold" : "brand"}
              />
            </div>
          </Tooltip>
        </>
      }
      toolbar={
        <div className="pms-exec-bar pms-tm-toolbar" style={{ width: "100%" }}>
          <Tooltip title="The date the Active-on-date evaluation uses. Changing it never changes what is tracked.">
            <DatePicker
              value={asOf}
              onChange={(value) => value && setAsOf(value)}
              format={DATE_FMT}
              allowClear={false}
              prefix={<span className="pms-tm-muted">As of</span>}
              style={{ width: 200 }}
            />
          </Tooltip>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All phases"
            value={phaseFilter}
            options={phaseOptions}
            onChange={(value) => {
              setPhaseFilter(value);
              // An activity outside the new phase would filter to nothing.
              setActivityFilter(undefined);
            }}
            style={{ width: 220 }}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All activities"
            value={activityFilter}
            options={activityOptions}
            onChange={(value) => setActivityFilter(value)}
            style={{ width: 260 }}
          />
          <Segmented
            value={filter}
            onChange={(value) => setFilter(value as MonitoringFilter)}
            options={FILTER_OPTIONS}
          />
          {/* Right-aligned cluster: search + view actions. Wraps as one block
              on narrow screens and stays right-aligned when it does. */}
          <div className="pms-tm-toolbar-right">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            allowClear
            className="pms-tm-search"
            prefix={<SearchOutlined style={{ color: "#8094ae" }} />}
            placeholder="Search task, activity or phase"
          />
          <WbsCodeToggle />
          <Tooltip title={allExpanded ? "Collapse all" : "Expand all"}>
            <Button
              icon={allExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
              disabled={!groupKeys.length}
              onClick={() => setExpandedKeys(allExpanded ? [] : groupKeys)}
              aria-label={allExpanded ? "Collapse all" : "Expand all"}
            />
          </Tooltip>
          <Popover
            open={bulkOpen}
            onOpenChange={setBulkOpen}
            trigger="click"
            placement="bottomRight"
            content={bulkContent}
          >
            <Button icon={<ThunderboltOutlined />} disabled={!visibleTasks.length}>
              Bulk
            </Button>
          </Popover>
          <Tooltip title="Reload">
            <Button icon={<ReloadOutlined />} onClick={load} aria-label="Reload" />
          </Tooltip>
          </div>
        </div>
      }
    >
      {noTasks ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="This project has no tasks yet — there is nothing to monitor"
        />
      ) : (
        <Table<TreeRow>
          size="small"
          rowKey="key"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: scrollX }}
          className="pms-tm-table"
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
            indentSize: 18,
          }}
          rowClassName={rowClassName}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search || filter !== "all" || phaseFilter || activityFilter
                    ? "No lines match this search and filter"
                    : "No tasks to monitor"
                }
              />
            ),
          }}
        />
      )}

      {changeCount > 0 && (
        <div className="pms-tm-savebar" role="region" aria-label="Unsaved changes">
          <span className="pms-tm-savebar-count">
            {changeCount} change{changeCount === 1 ? "" : "s"}
          </span>
          <span className="pms-tm-savebar-detail">
            {[
              changeBreakdown.toTrack ? `${changeBreakdown.toTrack} to track` : "",
              changeBreakdown.toUntrack ? `${changeBreakdown.toUntrack} to untrack` : "",
              changeBreakdown.windowEdits
                ? `${changeBreakdown.windowEdits} window edit${changeBreakdown.windowEdits === 1 ? "" : "s"}`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {invalidIds.length > 0 && (
            <Tooltip title="Show every row so the ones needing a window can be reached">
              <Button
                type="link"
                size="small"
                className="pms-tm-savebar-invalid"
                onClick={() => {
                  setSearch("");
                  setFilter("all");
                  setPhaseFilter(undefined);
                  setActivityFilter(undefined);
                }}
              >
                <WarningOutlined /> {invalidIds.length} row{invalidIds.length === 1 ? " needs" : "s need"} a valid window
              </Button>
            </Tooltip>
          )}
          <span className="pms-tm-savebar-spacer" />
          <Button onClick={discard} disabled={saving}>
            Discard
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={invalidIds.length > 0}
            onClick={save}
          >
            Save
          </Button>
        </div>
      )}
    </ProjectPageShell>
  );
};

export default TaskMonitoringPage;
